import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Download } from "lucide-react";

const MANUAL_FIELDS = [
  ["capital_raised", "Capital Raised ($)"],
  ["jobs_created", "Jobs Created"],
  ["businesses_launched", "Businesses Launched"],
  ["revenue_generated", "Revenue Generated ($)"],
  ["grants_won", "Grants Won ($)"],
  ["women_owned", "Women-owned Businesses"],
  ["minority_owned", "Minority-owned Businesses"],
  ["veteran_owned", "Veteran-owned Businesses"],
];

export default function EconomicImpact() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [period, setPeriod] = useState("");
  const [inputs, setInputs] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/ai/economic-impact/latest");
      if (data.report) {
        setReport(data.report.report);
        setMetrics(data.report.metrics);
      }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/economic-impact", { period_label: period, manual_inputs: inputs });
      setReport(data.report);
      setMetrics(data.metrics);
      setAssetId(data.asset_id);
      toast.success("Economic Impact Report generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const download = async (fmt) => {
    if (!assetId) return;
    const res = await api.get(`/assets/${assetId}/download`, { params: { fmt }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = `Economic_Impact.${fmt}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">AI Economic Impact™</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Program metrics → funder-ready story.</h1>
        <p className="mt-2 text-sm text-stone-600">Automatically aggregate your program's outcomes and generate a compelling economic impact narrative.</p>
      </div>

      <div className="card-flat">
        <div className="label-mono mb-3">Report Inputs</div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="label-mono block mb-1.5">Period Label</span>
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. FY 2025 Q4" className={inputCls} />
          </label>
        </div>
        <div className="mt-4">
          <div className="label-mono mb-2">Manual Inputs (optional, override auto-aggregation)</div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {MANUAL_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="label-mono block mb-1.5">{label}</span>
                <input type="number" value={inputs[k] || ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} className={inputCls} />
              </label>
            ))}
          </div>
        </div>
        <button onClick={generate} disabled={loading} data-testid="impact-generate" className="btn-primary mt-6">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate Impact Report</>}
        </button>
      </div>

      {report && report.headline && (
        <div className="card-flat">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-mono">Generated Report</div>
              <h2 className="mt-2 font-display text-2xl font-medium text-slate-950">{report.headline}</h2>
            </div>
            {assetId && (
              <div className="flex gap-2">
                <button onClick={() => download("docx")} className="btn-secondary !text-xs !px-3 !py-2"><Download className="h-3.5 w-3.5" /> Word</button>
                <button onClick={() => download("pdf")} className="btn-secondary !text-xs !px-3 !py-2"><Download className="h-3.5 w-3.5" /> PDF</button>
              </div>
            )}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-slate-950">{report.executive_summary}</p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div>
              <div className="label-mono mb-3">Key Outcomes</div>
              <div className="space-y-3">
                {(report.key_outcomes || []).map((k, i) => (
                  <div key={i} className="border-l-2 border-emerald-500 pl-4">
                    <div className="flex items-baseline justify-between">
                      <div className="text-sm font-medium text-slate-950">{k.metric}</div>
                      <div className="font-mono text-sm text-emerald-600">{k.value}</div>
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-stone-600">{k.insight}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="label-mono mb-3">ROI Narrative</div>
              <p className="text-sm leading-relaxed text-slate-950">{report.ROI_narrative}</p>
              <div className="label-mono mb-3 mt-6">Demographic Impact</div>
              <p className="text-sm leading-relaxed text-slate-950">{report.demographic_impact}</p>
              <div className="label-mono mb-3 mt-6">Industries & Growth</div>
              <p className="text-sm leading-relaxed text-slate-950">{report.industries_and_growth}</p>
            </div>
          </div>

          {Array.isArray(report.recommended_next_investments) && (
            <div className="mt-8">
              <div className="label-mono mb-3">Recommended Next Investments</div>
              <div className="grid gap-3 md:grid-cols-3">
                {report.recommended_next_investments.map((r, i) => (
                  <div key={i} className="rounded-md border border-stone-200 bg-stone-50 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-600">Investment {i + 1}</div>
                    <p className="mt-2 text-sm leading-relaxed text-slate-950">{typeof r === "string" ? r : r.recommendation || JSON.stringify(r)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metrics && (
            <div className="mt-8 rounded-md bg-stone-50 p-5">
              <div className="label-mono mb-3">Raw Metrics</div>
              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
                {Object.entries(metrics).map(([k, v]) => (
                  <div key={k}>
                    <div className="font-mono text-[10px] uppercase text-stone-500">{k.replace(/_/g, " ")}</div>
                    <div className="mt-0.5 font-medium text-slate-950">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
